import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Grid,
  TextField,
  Skeleton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Edit as EditIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import Autocomplete from "@mui/material/Autocomplete";
import { useForm, Controller } from "react-hook-form";
import { useProfile } from "../../contexts/ProfileContext";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

const ProfilePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: 12,
}));

const CompanyLogo = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(15),
  height: theme.spacing(15),
  marginBottom: theme.spacing(2),
}));

const CorporateProfile = () => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef();
  const { corporateRequiresProfile } = useProfile();

  const token = Cookies.get("token");
  console.log("check this context daat", corporateRequiresProfile)
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      url: "",
      industry: "",
      size: "",
      business_email: "",
      phone_num: "",
      description: "",
      workEnvironment: [],
      companyType: "",
      contactPersonName: "",
      contactPersonEmail: "",
      contactPersonPhone: "",
    },
  });

  useEffect(() => {
    fetchProfile();
    // Set business_email from token
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const email = decodedToken.email || "";
        if (email) {
          setValue("business_email", email);
        }
      } catch (e) {
        console.error("Error decoding token:", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/corporate/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Get email from token
      const _data = await response.json();
      if (!_data?.profile || Object.keys(_data.profile).length === 0) {
        let emailFromToken = "";
        if (token) {
          try {
            const decodedToken = jwtDecode(token);
            emailFromToken = decodedToken.email || "";
          } catch (e) {
            console.error("Error decoding token:", e);
          }
        }
        const initial = {
          name: "",
          url: "",
          industry: "",
          size: "",
          business_email: emailFromToken,
          phone_num: "",
          description: "",
          workEnvironment: [],
          companyType: "",
          contactPersonName: "",
          contactPersonEmail: "",
          contactPersonPhone: "",
        };
        setProfile(initial);
        setIsEditing(true);
        reset(initial);
        setLogoUrl(null);
        setLoading(false);
        return;
      }

      const data = _data.profile;

      // Ensure arrays exist
      data.workEnvironment = Array.isArray(data.workEnvironment)
        ? data.workEnvironment
        : [];

      // Always get email from token
      let businessEmail = "";
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          businessEmail = decodedToken.email || "";
        } catch (e) {
          console.error("Error decoding token:", e);
        }
      }

      setProfile(data);

      // populate react-hook-form values
      reset({
        name: data.name || "",
        url: data.url || "",
        industry: data.industry || "",
        size: data.size || "",
        business_email: businessEmail,
        phone_num: data.phone_num || "",
        description: data.description || "",
        workEnvironment: data.workEnvironment || [],
        companyType: data.companyType || "",
        contactPersonName: data.contactPersonName || "",
        contactPersonEmail: data.contactPersonEmail || "",
        contactPersonPhone: data.contactPersonPhone || "",
      });

      if (data.logo && data.logoMime) {
        setLogoUrl(`data:${data.logoMime};base64,${data.logo}`);
      } else {
        fetchLogo();
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching profile:", error);
      setLoading(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/corporate/profile/logo`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const blob = await res.blob();
        setLogoUrl(URL.createObjectURL(blob));
      } else {
        setLogoUrl(null);
      }
    } catch {
      setLogoUrl(null);
    }
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogoUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/corporate/profile/logo`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      setLogoUrl(null);
      setLogoFile(null);
    } catch (e) {
      // handle error
    }
  };

  const companyTypes = [
    { label: "Corporate", value: "corporate" },
    { label: "Start-up/Scale-up", value: "start-up/scale-up" },
    {
      label: "Entrepreneurial/Innovative",
      value: "entrepreneurial/innovative",
    },
    { label: "Mid-size", value: "mid-size" },
    { label: "Family-owned", value: "family-owned" },
    { label: "Other", value: "other" },
  ];

  const workEnvironments = [
    {
      label: "Fast-paced, goal-oriented and performance driven",
      value: "fast-paced, goal-oriented and performance driven",
    },
    {
      label: "Creative, energetic and socially engaging",
      value: "creative, energetic and socially engaging",
    },
    {
      label: "Calm, predictable, and cooperative",
      value: "calm, predictable, and cooperative",
    },
    {
      label: "Structured, organized and rule-based",
      value: "structured, organized and rule-based",
    },
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", data.name || "");
      formData.append("url", data.url || "");
      formData.append("industry", data.industry || "");
      formData.append("size", data.size || "");
      formData.append("business_email", data.business_email || "");
      formData.append("phone_num", data.phone_num || "");
      formData.append("description", data.description || "");
      formData.append("companyType", data.companyType || "");
      formData.append("contactPersonName", data.contactPersonName || "");
      formData.append("contactPersonEmail", data.contactPersonEmail || "");
      formData.append("contactPersonPhone", data.contactPersonPhone || "");
      // arrays
      formData.append(
        "workEnvironment",
        JSON.stringify(data.workEnvironment || [])
      );

      formData.append("logo", logoFile || "");
      formData.append("logo_mime", logoFile?.type || "");

      // Use POST if requiresProfile is true, otherwise use PUT
      const method = corporateRequiresProfile === true ? "POST" : "PUT";

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/corporate/profile`,
        {
          method: method,
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      // update local state and UI
      setIsEditing(false);
      setLogoFile(null);
      // refresh data from server
      fetchProfile();
    } catch (e) {
      console.error("Error saving profile:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          mt: 5,
          mb: 5,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <ProfilePaper
          elevation={2}
          sx={{ width: "70%", justifyContent: "center" }}
        >
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Skeleton variant="circular" width={120} height={120} />
              <Skeleton variant="text" width={200} height={40} sx={{ mt: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={60} />
            </Grid>
          </Grid>
        </ProfilePaper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        mt: 5,
        mb: 5,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <ProfilePaper
        elevation={2}
        sx={{ width: "70%", justifyContent: "center" }}
      >
        {/* Header: title + buttons aligned right */}
        {corporateRequiresProfile === true && !isEditing && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Profile submitted for review</AlertTitle>
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Company Profile
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isEditing && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setIsEditing(true);
                }}
                sx={{ minWidth: 160 }}
              >
                Edit Profile
              </Button>
            )}
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <CompanyLogo src={logoUrl}>
                <BusinessIcon sx={{ fontSize: 60 }} />
              </CompanyLogo>

              {isEditing && (
                <>
                  <Button variant="outlined" component="label" sx={{ mb: 2 }}>
                    {logoUrl ? "Replace Logo" : "Upload Logo"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleLogoChange}
                    />
                  </Button>
                  {logoUrl && (
                    <Button
                      color="error"
                      onClick={handleRemoveLogo}
                      sx={{ mb: 2 }}
                    >
                      Remove Logo
                    </Button>
                  )}
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name *"
                {...register("name", {
                  required: "Company name is required",
                  validate: (value) =>
                    /[A-Za-z0-9]/.test(value) ||
                    "Company name cannot contain only special characters",
                })}
                disabled={!isEditing}
                error={!!errors.name}
                helperText={errors.name?.message}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Website *"
                {...register("url", { required: "Website URL is required" })}
                disabled={!isEditing}
                error={!!errors.url}
                helperText={errors.url?.message}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.companyType}
              >
                <InputLabel>Company Type *</InputLabel>
                <Controller
                  name="companyType"
                  control={control}
                  rules={{ required: "Company type is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={!isEditing}
                      label="Company Type"
                      onChange={(e) => {
                        field.onChange(e);
                        console.log("Selected company type:", e.target.value);
                        // keep local profile state in sync
                        setProfile((p) => ({
                          ...(p || {}),
                          companyType: e.target.value,
                        }));
                      }}
                    >
                      {companyTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.companyType && (
                  <Typography variant="caption" color="error">
                    {errors.companyType?.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="workEnvironment"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={workEnvironments}
                    getOptionLabel={(option) => option.label}
                    value={
                      Array.isArray(field.value) && field.value.length
                        ? workEnvironments.filter((env) =>
                          field.value.includes(env.value)
                        )
                        : []
                    }
                    onChange={(event, newValue) => {
                      const values = newValue.map((v) => v.value);
                      field.onChange(values);
                      setProfile((p) => ({
                        ...(p || {}),
                        workEnvironment: values,
                      }));
                    }}
                    disabled={!isEditing}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Work Environment"
                        placeholder="Add work environment"
                        InputLabelProps={{ shrink: true }}
                        disabled={!isEditing}
                        margin="normal"
                      />
                    )}
                    noOptionsText="No work environments available"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Description"
                {...register("description")}
                disabled={!isEditing}
                multiline
                rows={4}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Industry *"
                {...register("industry", { required: "Industry is required" })}
                disabled={!isEditing}
                error={!!errors.industry}
                helperText={errors.industry?.message}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                min={0}
                label="Company Size *"
                {...register("size", {
                  required: "Company size is required",
                  validate: (value) => {
                    if (isNaN(value)) return "Company size must be a number";
                    if (!Number.isInteger(Number(value)))
                      return "Company size must be an integer";
                    if (Number(value) <= 0)
                      return "Company size must be greater than zero";
                    return true;
                  },
                })}
                disabled={!isEditing}
                error={!!errors.size}
                helperText={errors.size?.message}
                margin="normal"
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault(); // block non-integer characters
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                {...register("phone_num")}
                disabled={!isEditing}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <TextField
                fullWidth
                label="Business Email"
                {...register("business_email", {
                  required: "Business email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
                disabled={true}
                error={!!errors.business_email}
                helperText={errors.business_email?.message}
                margin="normal"
              />

              <TextField
                fullWidth
                label="Contact Person Name *"
                {...register("contactPersonName", {
                  required: "Contact person name is required",
                  pattern: {
                    value: /[A-Za-z0-9]/,
                    message:
                      "Contact person name cannot contain only special characters",
                  },
                })}
                disabled={!isEditing}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Contact Person Email *"
                {...register("contactPersonEmail", {
                  required: "Contact person email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
                disabled={!isEditing}
                error={!!errors.contactPersonEmail}
                helperText={errors.contactPersonEmail?.message}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Contact Person Phone"
                {...register("contactPersonPhone")}
                disabled={!isEditing}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                {isEditing && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsEditing(false);
                        fetchProfile(); // reload values from server
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
        {corporateRequiresProfile === true && !isEditing && (
          <Alert severity="info" sx={{ mb: 3 }}>
            We review each company profile to ensure alignment and prepare for a meaningful partnership.
            Once approved, you’ll be able to start hiring and finding candidates on EQuAlly.
          </Alert>
        )}
      </ProfilePaper>
    </Box>
  );
};

export default CorporateProfile;
